import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Card from 'react-bootstrap/Card';
import InputGroup from 'react-bootstrap/InputGroup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImage } from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';
import Stack from 'react-bootstrap/Stack';
import Spinner from 'react-bootstrap/Spinner';

import { getMenu, updateMenu, deleteMenu, getAllCategories } from 'src/Services/api';
import useQuery from 'src/Hooks/useQuery';
import useMutation from 'src/Hooks/useMutation';
import confirm from "src/Components/ConfirmDialog";
import Notification, { useNotification } from 'src/Components/Notification';
import { getCurrency } from "src/Utils/formatters"

import './styles.scss';

const REQUIRED_FILEDS = ["name", "price", "category"];

const getChangedFields = (newValues, oldValues) =>
  Object.keys(newValues).reduce((acc, field) => {
    if (newValues[field] instanceof File || newValues[field] !== oldValues[field]) {
      acc.set(field, newValues[field])
    }
    return acc
  }, new Map())

const getImageURL = (image) =>
  image instanceof File ? URL.createObjectURL(image) : image;

const EditMenu = () => {
  const [formValues, setFormValues] = useState({});
  const [categories, setCategories] = useState([]);
  const { id: spotId, menuId } = useParams();
  const navigate = useNavigate();
  const { notifySuccess, notifyError, onClose, notification } = useNotification();

  const { loading: getMenuLoading, error, response = {} } = useQuery(getMenu, { id: menuId })
  const { data: menu } = response

  const { setter: updateMenuAction, loading: updateMenuLoading } = useMutation(updateMenu)
  const { setter: deleteMenuAction, loading: deleteMenuLoading } = useMutation(deleteMenu)

  useEffect(() => {
    if (menu) {
      setFormValues(menu)
    }
  }, [menu]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getAllCategories();
        setCategories(response.data?.categories);
      } catch (error) {
        console.error('Error fetching spot categories:', error);
      }
    };
    fetchCategories();
  }, [spotId]);

  const handleUpdateMenu = async (event) => {
    event.preventDefault();
    const changedFormFields = getChangedFields(formValues, menu)
    if (changedFormFields.size) {
      const newMenuData = new FormData();
      changedFormFields.forEach((fieldValue, fieldName) => {
        newMenuData.append(fieldName, fieldValue);
      });
      updateMenuAction(
        { id: menuId, data: newMenuData },
        () => notifySuccess({ message: "Menu updated successfully" }),
        () => notifyError({ message: "Error: Menu update failed" }),
      );
    }
  }

  const handleDeleteMenu = async (event) => {
    event.preventDefault();
    if (await confirm(`Are you sure you want to delete this menu?`)) {
      deleteMenuAction(
        { id: menuId },
        () => notifySuccess({ operation: "delete", message: "Menu deleted successfully" }),
        () => notifyError({ message: "Menu delete failed" }),
      );
    }
  }

  const cancelEditMenu = () => {
    navigate(`/spot/${spotId}/menu`);
  }

  const onFormValueChange = (event) => {
    if (event.target.name) {
      const fieldValue = event.target.name === "image" ?
        event.target.files && event.target.files[0] : event.target.value;
      setFormValues((values) => {
        return { ...values, [event.target.name]: fieldValue };
      });
    }
  }

  const canSubmit = REQUIRED_FILEDS.every((field) => !!formValues[field]);

  if (getMenuLoading) {
    return (
      <Container className="d-flex justify-content-center pt-5">
        <Spinner animation="border">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    )
  }

  if (notification && notification.operation === "delete") {
    return (
      <Notification
        id="menu-delete"
        {...notification}
        onClose={() => {
          onClose();
          navigate(`/spot/${spotId}/menu`);
        }}
      />
    )
  }

  if (error) {
    return (
      <Notification
        id="menu-edit-error"
        type="danger"
        message="Error: could not get menu item. Please try again."
      />
    )
  }

  return (
    <Container className="mt-4">
      <Form name="addMenu">
        <Row>
          <Col md={7}>
            <h5 className="mb-4 pt-4">{menu && menu.name}</h5>
            <Form.Group className="mb-4" controlId='addMenuName'>
              <Form.Label>Name</Form.Label>
              <Form.Control
                name="name"
                onChange={onFormValueChange}
                value={formValues.name}
              />
            </Form.Group>
            <Form.Group className="mb-4" controlId='addMenuPrice'>
              <Form.Label>Price</Form.Label>
              <InputGroup>
                <InputGroup.Text>{getCurrency(formValues.currency)}</InputGroup.Text>
                <Form.Control
                  type="number"
                  name="price"
                  step="0.01"
                  min="0"
                  onChange={onFormValueChange}
                  value={formValues.price}
                />
              </InputGroup>
            </Form.Group>
            <Row className="mb-4">
              <Col>
                <Form.Label>Category</Form.Label>
                <Form.Select
                  aria-label="Menu Category"
                  name="category"
                  onChange={onFormValueChange}
                  value={categories.find(category => category.name === formValues.category)?.id}
                >
                  <option key="category" value="" hidden>Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col>
                <Form.Label>Available</Form.Label>
                <Form.Select
                  aria-label="Is Menu Available?"
                  name="is_available"
                  onChange={onFormValueChange}
                  value={formValues.is_available}
                >
                  <option value="" hidden>Available?</option>
                  <option value={true}>True</option>
                  <option value={false}>False</option>
                </Form.Select>
              </Col>
            </Row>
              <Form.Group className="mb-4" controlId='addMenuDescription'>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="description"
                  onChange={onFormValueChange}
                  value={formValues.description}
                />
              </Form.Group>
          </Col>
          <Col md={{ span: 4, offset: 1 }}>
            <Card className='mtop-2'>
              <Card.Body>
                <div className="d-flex flex-column align-items-center">
                  <Form.Group controlId='addMenuImage' className='w-100'>
                    <Form.Control
                      type='file'
                      name="image"
                      onChange={onFormValueChange}
                    />
                  </Form.Group>
                </div>
              </Card.Body>
              <div className="d-flex flex-column align-items-center">
                {formValues.image ? (
                  <Card.Img variant="top" src={getImageURL(formValues.image)} className="menu-image" />
                ) : (
                  <div className="d-flex align-items-center no-image">
                    <FontAwesomeIcon
                      fixedWidth
                      icon={faFileImage}
                      color="gray"
                      size="4x"
                    />
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>
        <Row className="my-5 gy-4">
          <Col lg={7}>
            <Stack direction="horizontal" className='justify-content-between'>
              <Button
                variant="primary"
                className="jamlyf-primary-button py-2 col-5"
                type="submit"
                disabled={!canSubmit || updateMenuLoading}
                onClick={handleUpdateMenu}
              >
                {updateMenuLoading && (
                  <Fragment>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    <span className="visually-hidden">Loading...</span>
                  </Fragment>
                )}
                Save
              </Button>
              <Button
                variant="outline-primary"
                className="jamlyf-outline-button py-2 col-5 m-0"
                onClick={cancelEditMenu}
              >
                Cancel
              </Button>
            </Stack>
          </Col>
          <Col lg={{ span: 4, offset: 1 }} className='d-flex align-items-center justify-content-center'>
            <Button
              variant="primary"
              className="jamlyf-delete-button py-2 col-10"
              disabled={deleteMenuLoading}
              onClick={handleDeleteMenu}
            >
              {deleteMenuLoading && (
                <Fragment>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="visually-hidden">Loading...</span>
                </Fragment>
              )}
              Delete
            </Button>
          </Col>
        </Row>
      </Form>
      <Notification id="menu-edit" onClose={onClose} {...notification} />
    </Container>
  );
}

export default EditMenu;
